import { Component, Input, input, OnInit, output, signal, inject, OnDestroy, computed, effect, Injector, runInInjectionContext, } from '@angular/core';
import { localizeWithFallback } from '../utils/localization-utils';

import {
  AngularGridInstance, AngularSlickgridModule, Column, Formatter, GridOption, GridStateChange, OnEventArgs, } from 'angular-slickgrid';
import { SessionStateService } from '@abp/ng.core';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { DataTableHelperService } from '../services/data-table-helper';
import { ModalComponent, ToasterService } from '@abp/ng.theme.shared';
import { ICrudBulkOperationError } from '../interfaces/ICrudError';
import { LocalizationService } from '@abp/ng.core';
import { CustomBackendService } from '../services/custom-backend.service';
import { SummaryService } from '../summary/summary-service';
import {
  applyPostEditNormalization,
  patchEditorForRequiredDependencies,
  validateEditorBeforeDestroy,
} from './helpers/inline-editor-validation.helper';
import { ForeignKeyConfig } from '../fields/field-definition';
import { ZnButton } from '@zendra/shared';

const ROW_ERROR_MESSAGE_KEY = '__rowErrorMessage';
const ROW_ERROR_TITLE_KEY = '__rowErrorTitle';

@Component({
  selector: 'app-generic-table',
  imports: [AngularSlickgridModule, ModalComponent, ZnButton],
  providers: [DataTableHelperService],
  templateUrl: './generic-table.html',
  styleUrl: './generic-table.scss',
})
export class GenericTable implements OnInit, OnDestroy {
  private readonly helper = inject(DataTableHelperService);
  private readonly sessionState = inject(SessionStateService);
  private readonly summary = inject(SummaryService);
  private readonly injector = inject(Injector);
  private readonly toaster = inject(ToasterService);
  private langChangeSubscription?: Subscription;
  private gridClickSubscription?: any; // SlickGrid event subscription
  private currentEditingrowElement: HTMLElement | null = null;
  private readonly localizationService = inject(LocalizationService);
  private gridId: string;
  private static gridCounter = 0;

  entityColumnDefinitions = input<Column[]>([]);
  extraOptions = input<GridOption>({});
  dataSource = input<any>([]);
  loading = input<boolean>(false);
  canEditInline = input<boolean>(false);
  canEdit = input<boolean>(false);
  gridIdInput = input<string | undefined>(undefined);
  showCheckboxColumn = input<boolean>(true);
  showEditColumn = input<boolean>(true);
  enableInitialAutoQuery = input<boolean>(false);
  rowActionConfig = input<{ title?: string; iconClass?: string } | null>(null);

  @Input() backendServiceProcess?: (query: string) => Observable<any>;

  rowsWithErrors = input<ICrudBulkOperationError>({
    failedRecords: [],
    statusCode: '',
  });

  columnDefinitions = signal<Column[]>([]);
  private readonly lastRowCount = signal<number>(10);

  private readonly skeletonDataset = computed(() => {
    if (!this.loading()) return [];
    const rowCount = this.lastRowCount();
    return Array.from({ length: rowCount }, (_, i) => ({
      id: -(1000 + i),
      isSkeleton: true,
    }));
  });

  dataset = computed(() => {
    return this.loading() ? this.skeletonDataset() : this.dataSource();
  });

  uniqueGridId = computed(() => {
    const providedId = this.gridIdInput();
    if (providedId) {
      return providedId;
    }

    return `grid-${++GenericTable.gridCounter}`;
  });

  editRow = output<any>();
  rowAction = output<any>();
  beforeEditCell = output<any>();
  destroyEditor = output<any>();
  cellChange = output<any>();
  headerButtonClicked = output<any>();
  initializationCompleted = output<any>();
  selectedItemsChange = output<number[]>();
  triggerEditMode = output<any>();
  exportInProgress = input(false);
  exportToExcelRecord = output();
  technicalNamesReport = output();
  gridRowClick = output<any>();  // NEW: Emits when a row is clicked

  grid: AngularGridInstance;
  gridOptions: GridOption;
  saveInlineRow = output<any>();
  hasErrors = false;
  errorMessage: string;
  errorTitle: string;
  errorModalOptions = {
    centered: true,
    size: 'lg' as const,
  };
  private rowErrorDataViewHandler?: { dataView: any; handler: () => void };

  constructor() {
    effect(() => {
      const data = this.dataSource();
      const isLoading = this.loading();

      if (!isLoading && data && data.length > 0) {
        this.lastRowCount.set(data.length);
      }
    });

    effect(() => {
      this.dataSource();
      const isLoading = this.loading();
      const rowsWithErrors = this.rowsWithErrors();
      if (!this.grid?.dataView || !rowsWithErrors || isLoading) {
        return;
      }

      this.applyOrWaitForErrors(rowsWithErrors);
    });

    effect(() => {
      const columns = this.entityColumnDefinitions();
      const data = this.dataSource();
      const isLoading = this.loading();

      if (isLoading || !columns.length || !data.length) {
        return;
      }

      this.prefetchForeignKeys(columns, data);
    });
  }

  ngOnInit(): void {
    this.getGridOptions();

    if (this.backendServiceProcess) {
      this.configureBackendServiceOptions(this.backendServiceProcess);
    }

    this.langChangeSubscription = this.sessionState.onLanguageChange$().subscribe(() => {
      this.updateGridMenuTitles();
    });
  }

  ngOnDestroy(): void {
    this.langChangeSubscription?.unsubscribe();

    if (this.gridClickSubscription && typeof this.gridClickSubscription.unsubscribe === 'function') {
      this.gridClickSubscription.unsubscribe();
    }

    this.clearRowErrorDataViewHandler();

    this.helper.detachNumberInputExponentBlock();
    this.summary.destroy();
  }
  gridStateChanged(gridStateChanges: GridStateChange) {
    const selectedIds = (gridStateChanges.gridState.rowSelection?.filteredDataContextIds ||
      []) as number[];

    this.selectedItemsChange.emit(selectedIds);
    this.summary.handleSelectionChange();
  }

  ready(grid: any) {
    this.grid = grid.detail;

    this.initializeColumns();

    requestAnimationFrame(() => {
      this.grid.slickGrid.setOptions({
        enableAutoSizeColumns: true
      });

      this.grid.slickGrid.autosizeColumns();

      this.grid.slickGrid.setOptions({
        enableAutoSizeColumns: false
      });
    });

    this.enableFocus(false, '_checkbox_selector');

    if (this.rowsWithErrors() && this.rowsWithErrors().failedRecords.length > 0) {
      this.applyOrWaitForErrors(this.rowsWithErrors());
    }

    this.gridId = this.grid.slickGrid.getOptions().gridId;
    this.helper.setGridInstance(this.grid);
    this.helper.attachNumberInputExponentBlock();
    this.summary.initialize(this.grid, this.gridId);

    this.gridClickSubscription = this.grid.slickGrid.onClick.subscribe((_e: any, args: any) => {
      const row = this.grid.dataView.getItem(args.row);
      if (row && !row.isSkeleton && !row[ROW_ERROR_MESSAGE_KEY]) {
        this.gridRowClick.emit(row);
      }
    });

    this.initializationCompleted.emit(this.grid);
  }

  showSummary() {
    this.summary.toggle();
  }

  onErrorModalVisibleChange(visible: boolean): void {
    this.hasErrors = visible;
  }

  getCloseLabel(): string {
    return localizeWithFallback(this.localizationService, 'LanguageService::Close');
  }

  private prefetchForeignKeys(columns: Column[], data: any[]): void {
    columns.forEach(column => {
      const field = column.field;
      const fkConfig = column.params?.foreignKeyConfig as ForeignKeyConfig | undefined;
      const getByIds = fkConfig?.getByIds;
      const displayNestedPath = column.params?.displayNestedPath as string | undefined;

      if (!field || !getByIds || displayNestedPath) {
        return;
      }

      const ids = Array.from(
        new Set(
          data
            .map(row => row?.[field])
            .filter((value): value is string | number => typeof value === 'string' || typeof value === 'number')
            .filter(value => value !== '')
            .filter(value => !fkConfig.getCachedItem?.(value))
        )
      );

      if (!ids.length) {
        return;
      }

      runInInjectionContext(this.injector, () =>
        getByIds(ids, { skipHandleError: true })
      )
        .pipe(take(1))
        .subscribe({
          next: () => {
            if (this.grid) {
              this.refresh();
            }
          },
          error: () => undefined,
        });
    });
  }

  private configureBackendServiceOptions(
    processFunc: (query: string) => Observable<any>
  ): void {
    const backendService = new CustomBackendService();

    this.gridOptions.enableFiltering = true;

    this.gridOptions.backendServiceApi = {
      service: backendService,
      options: {
        executeProcessCommandOnInit: this.enableInitialAutoQuery(),
      },
      process: (query: string) => {
        return processFunc(query);
      },
      onError: (_: any) => {
      },
    };
  }

  private getGridOptions() {
    const extraOpts = this.extraOptions();
    const gridLocales = this.helper.getGridLanguage();
    const emptyTitle = localizeWithFallback(this.localizationService, 
      'LanguageService::EmptySearchResultTitle'
    );
    const emptyMessage = localizeWithFallback(this.localizationService, 
      'LanguageService::EmptySearchResult'
    );

    this.gridOptions = {
      enableAutoResize: true,
      autoFitColumnsOnFirstLoad: false,
      enableAutoSizeColumns: false,
      autosizeColumnsByCellContentOnFirstLoad: false,
      enableAutoResizeColumnsByCellContent: false,
      resizeByContentOnlyOnFirstLoad: true,
      enableAsyncPostRender: true,
      forceFitColumns: false,
      autoHeight: false,
      enableEmptyDataWarningMessage: true,
      emptyDataWarning: {
        message: (() => {
          const wrapper = document.createElement('div');
          wrapper.innerHTML = `
            <img src="assets/images/no-results.svg" width="120" alt="Sin datos" />
            <h4>${emptyTitle}</h4>
            <p>${emptyMessage}</p>
            `;

          const slickViewport = document.querySelector('.slickgrid-container');

          const viewportWidth = slickViewport
            ? slickViewport.getBoundingClientRect().width
            : window.innerWidth;
          wrapper.style.width = `${viewportWidth}px`;

          return wrapper;
        })(),
        className: 'empty-grid-warning',
        leftViewportMarginLeft: '0',
        rightViewportMarginLeft: '0',
      },
      enableSorting: true,
      multiColumnSort: true,
      numberedMultiColumnSort: true,
      resizeByContentOptions: {
        cellCharWidthInPx: 10,
      },
      enableExcelCopyBuffer: true,
      enableCellNavigation: true,
      gridWidth: 'auto',
      enableHeaderButton: true,
      enableRowSelection: this.showCheckboxColumn(),
      enableCheckboxSelector: this.showCheckboxColumn(),
      enableAutoTooltip: true,
      autoTooltipOptions: {
        enableForHeaderCells: true,
        enableForCells: true,
      },
      ...(this.showCheckboxColumn() ? {
        checkboxSelector: {
          hideInColumnTitleRow: false,
          applySelectOnAllPages: true,
        },
        rowSelectionOptions: {
          selectActiveRow: false,
        },
      } : {}),
      editable: this.canEditInline(),
      autoEdit: false,
      defaultFilterPlaceholder: 'Filtrar',
      columnPicker: {
        hideForceFitButton: true,
      },
      gridMenu: {
        menuUsabilityOverride: () => true,
        hideForceFitButton: true,
        hideToggleFilterCommand: false,
        hideClearAllSortingCommand: false,
        hideClearAllFiltersCommand: false,
        ...extraOpts.gridMenu,
        commandItems: [
          { command: '', divider: true, positionOrder: 60 },
          {
            command: 'exportToExcel',
            itemUsabilityOverride: () => !this.exportInProgress(),
            title: gridLocales.TEXT_EXPORT_TO_EXCEL,
            iconCssClass: 'bi bi-file-earmark-x',
            positionOrder: 61,
            action: (_e: Event, _args: any) => {
              if (!this.exportInProgress()) this.exportToExcelRecord.emit();
            },
          },
          { command: '', divider: true, positionOrder: 62 },
          {
            command: 'technicalNamesReport',
            title: gridLocales.TEXT_TECHNICAL_NAMES_REPORT,
            iconCssClass: 'bi bi-list-ul',
            positionOrder: 63,
            action: (_e: Event, _args: any) => {
              this.technicalNamesReport.emit();
            },
          },
          { command: '', divider: true, positionOrder: 63 },
          {
            command: 'resizeAllColumnsByContent',
            title: gridLocales.TEXT_RESIZE_ALL_BY_CONTENT,
            iconCssClass: 'mdi mdi-arrow-expand-horizontal',
            positionOrder: 64,
            action: () => {
              this.helper.resizeColumnsByContent();
              this.summary.AddFooterComponentWhenSummaryActive();
            },
          },
          { command: '', divider: true, positionOrder: 65 },
          ...(extraOpts.gridMenu?.commandItems || []),
        ],
      },
      editCommandHandler: (_item, _column, editCommand) => {
        editCommand.execute();
      },
      createFooterRow: true,
      showFooterRow: true,
      footerRowHeight: 48,
      locales: gridLocales,
      enablePagination: true,
      pagination: {
        pageSizes: [5, 10, 20, 50, 100],
        pageSize: 20,
        pageNumber: 1,
        totalItems: 0,
      },
      ...extraOpts,
    };
  }

  private updateGridMenuTitles() {
    if (!this.grid || !this.gridOptions.gridMenu) return;

    const gridLocales = this.helper.getGridLanguage();
    const commandItems = this.gridOptions.gridMenu.commandItems;

    if (commandItems) {
      for (const item of commandItems) {
        if (item !== 'divider') {
          if (item.command === 'exportToExcel') {
            item.title = gridLocales.TEXT_EXPORT_TO_EXCEL;
          } else if (item.command === 'technicalNamesReport') {
            item.title = gridLocales.TEXT_TECHNICAL_NAMES_REPORT;
          } else if (item.command === 'resizeAllColumnsByContent') {
            item.title = gridLocales.TEXT_RESIZE_ALL_BY_CONTENT;
          }
        }
      }
    }

    this.gridOptions.locales = gridLocales;
  }

  private initializeColumns() {
    const allColumns = this.grid.gridService.getAllColumnDefinitions().map(col =>
      this.withHeaderTooltip(col)
    );

    const wrappedEntityColumns = this.entityColumnDefinitions().map(col => ({
      ...this.withHeaderTooltip(col),
      minWidth: Math.max(col.minWidth ?? 0, 55),
      formatter: this.wrapFormatterWithSkeleton(col.formatter),
    }));

    const columnsToAdd: Column[] = [...allColumns];

    if (this.showEditColumn()) {
      const editColumn = this.createEditColumn();
      columnsToAdd.push(editColumn);
    }

    if (this.rowActionConfig()) {
      const readColumn = this.createReadColumn();
      columnsToAdd.push(readColumn);
    }

    columnsToAdd.push(...wrappedEntityColumns);

    this.columnDefinitions.set(columnsToAdd);
  }

  private withHeaderTooltip(column: Column): Column {
    if (column.toolTip) {
      return column;
    }

    const toolTip = this.getHeaderTooltipText(column.name);
    return toolTip ? { ...column, toolTip } : column;
  }

  private getHeaderTooltipText(columnName: Column['name']): string {
    if (typeof columnName === 'string') {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = columnName;
      return wrapper.textContent?.trim() ?? '';
    }

    if (columnName instanceof HTMLElement || columnName instanceof DocumentFragment) {
      return columnName.textContent?.trim() ?? '';
    }

    return '';
  }

  private getReadActionIconClass(): string {
    return this.rowActionConfig()?.iconClass ?? 'bi bi-eye';
  }

  private getReadActionTitle(): string {
    return this.rowActionConfig()?.title ?? 'Visualizar';
  }
  
  private createEditColumn(): Column {
    const editIconFormatter: Formatter = (row, cell, value, columnDef, dataContext) => {
      if (dataContext?.[ROW_ERROR_MESSAGE_KEY]) {
        return `<i class="bi bi-info-circle-fill warning-icon" title="Error"></i>`;
      }
      if (this.canEdit()) {
        return `<i class="bi bi-pencil" title="Editar"></i>`;
      }
      return;
    };

    return {
      id: '_edit',
      field: 'id',
      excludeFromColumnPicker: true,
      excludeFromGridMenu: true,
      excludeFromHeaderMenu: true,
      focusable: false,
      reorderable: false,
      formatter: editIconFormatter,
      minWidth: 30,
      maxWidth: 30,
      cssClass: 'edit-column',

      onCellClick: (_e: Event, args: OnEventArgs) => {
        const cellToActivate =
          args.grid.getColumns().length > args.cell + 1 ? args.cell + 1 : args.cell;
        args.grid.setActiveCell(args.row, cellToActivate);

        if (args.dataContext[ROW_ERROR_MESSAGE_KEY]) {
          this.errorMessage = args.dataContext[ROW_ERROR_MESSAGE_KEY];
          this.errorTitle = args.dataContext[ROW_ERROR_TITLE_KEY] || this.getDefaultErrorTitle();
          this.hasErrors = true;
        } else {
          this.hasErrors = false;
          if (this.canEdit()) {
            this.editRow.emit(args.dataContext);
          }
        }
      },
    };
  }

  private createReadColumn(): Column {
    const readIconFormatter: Formatter = (row, cell, value, columnDef, dataContext) => {
      if (dataContext?.[ROW_ERROR_MESSAGE_KEY]) {
        return `<i class="bi bi-info-circle-fill warning-icon" title="Error"></i>`;
      }

      return `<i class="${this.getReadActionIconClass()}" title="${this.getReadActionTitle()}"></i>`;
    };

    return {
      id: '_read',
      field: 'id',
      excludeFromColumnPicker: true,
      excludeFromGridMenu: true,
      excludeFromHeaderMenu: true,
      focusable: false,
      reorderable: false,
      formatter: readIconFormatter,
      minWidth: 30,
      maxWidth: 30,
      cssClass: 'read-column',
      onCellClick: (_e: Event, args: OnEventArgs) => {
        const cellToActivate =
          args.grid.getColumns().length > args.cell + 1 ? args.cell + 1 : args.cell;
        args.grid.setActiveCell(args.row, cellToActivate);

        if (args.dataContext[ROW_ERROR_MESSAGE_KEY]) {
          this.errorMessage = args.dataContext[ROW_ERROR_MESSAGE_KEY];
          this.errorTitle = args.dataContext[ROW_ERROR_TITLE_KEY] || this.getDefaultErrorTitle();
          this.hasErrors = true;
          return;
        }

        this.hasErrors = false;
        this.rowAction.emit(args.dataContext);
      },
    };
  }

  private wrapFormatterWithSkeleton(originalFormatter?: Formatter): Formatter {
    return (row, cell, value, columnDef, dataContext, grid) => {
      if (dataContext?.isSkeleton) {
        return `<div class="placeholder-wave"><span class="placeholder rounded"></span></div>`;
      }

      return originalFormatter
        ? originalFormatter(row, cell, value, columnDef, dataContext, grid)
        : value ?? '';
    };
  }

  handleBeforeEditCell(data: any) {
    patchEditorForRequiredDependencies(this.grid, data);
    setTimeout(() => {
      const node = this.grid.slickGrid.getCellNode(data.args.row, data.args.cell);
      const rowElement = node?.closest('.slick-row') as HTMLElement | null;
      if (!rowElement) return;

      if (this.currentEditingrowElement && this.currentEditingrowElement !== rowElement) {
        this.currentEditingrowElement.classList.remove('editing-row');
      }

      const hasEditableCell = !!rowElement.querySelector('.slick-cell.editable');
      if (hasEditableCell) {
        rowElement.classList.add('editing-row');
        this.currentEditingrowElement = rowElement;
      } else {
        this.currentEditingrowElement = null;
      }
    });
    this.beforeEditCell.emit(data);
  }

  handleCellChange(data: any) {
    this.cellChange.emit(data);
  }

  handleBeforeCellEditorDestroy(data: any) {
    const canDestroy = validateEditorBeforeDestroy(
      this.grid,
      data,
      (msg) => this.toaster.error(msg, null, { life: 5000 }),
      () => this.enableSingleClickEdit()
    );
    if (!canDestroy) {
      return;
    }

    applyPostEditNormalization(data);

    this.destroyEditor.emit(data);
  }

  handleValidationError(data: any) {
    this.toaster.error(data.args.validationResults.msg, null, {
      life: 5000,
    });
  }

  handleOnDblClick(data: any) {
    if (this.canEditInline()) {
      this.triggerEditMode.emit(data);
    }
  }

  handleColumnsResizeDblClick(data) {
    const columId = data.args.triggeredByColumn;
    this.helper.resizeColumnByContent(columId);
    this.summary.AddFooterComponentWhenSummaryActive();
  }

  handleHeaderMenuCommand(data) {
    const columns = this.grid.slickGrid.getColumns();
    const command = data.command;
    const column = data.column;
    if (command === 'column-resize-by-content') {
      this.helper.resizeColumnByContent(column.id);
    }
    this.summary.AddFooterComponentWhenSummaryActive();
  }

  handleColumnsChanged(_: any) {
    this.summary.handleColumnChange()
  }

  handleColumnsReordered(data) {
    const columnsOrder = data.args.impactedColumns;
    const previousColumnsOrder = data.args.previousColumnOrder;
    const lastColumn = columnsOrder[columnsOrder.length - 1];
    const previouslastColumnId = previousColumnsOrder[previousColumnsOrder.length - 1];

    if (previouslastColumnId !== lastColumn.id) {
      this.helper.resizeLastColumn(lastColumn.id);
    }
    this.summary.AddFooterComponentWhenSummaryActive();
  }

  refresh(): void {
    if (!this.grid) return;
    this.grid.dataView.refresh();
    this.grid.slickGrid.invalidate();
    this.grid.slickGrid.render();
  }

  toggleColumn(enabled: boolean, columnId: string): void {
    if (!this.grid) return;

    const columns = this.grid.slickGrid.getColumns();
    const column = columns.find(col => col.id === columnId);

    if (!column) return;

    column.cssClass = enabled
      ? column.cssClass?.replace('column-disabled', '').trim() || ''
      : `${column.cssClass ? column.cssClass + ' ' : ''}column-disabled`;

    column.headerCssClass = enabled
      ? column.headerCssClass?.replace('header-disabled', '').trim() || ''
      : `${column.headerCssClass ? column.headerCssClass + ' ' : ''}header-disabled`;

    this.grid.slickGrid.setColumns(columns);
  }

  blockCellEdit(): void {
    if (!this.grid) return;

    setTimeout(() => {
      const lock = this.grid.slickGrid.getEditorLock();
      if (lock.isActive()) {
        lock.commitCurrentEdit();
        lock.cancelCurrentEdit();
      }
    }, 1);
  }

  activateCell(row: number, cell: number): void {
    if (!this.grid) return;

    this.enableSingleClickEdit();
    this.grid.slickGrid.setActiveCell(row, cell, true);
  }

  disableInlineEditing(): void {
    if (!this.grid) return;

    const options = this.grid.slickGrid.getOptions();
    this.grid.slickGrid.setOptions({ ...options, editable: false, autoEdit: false });
  }

  enableInlineEditing(): void {
    if (!this.grid) return;

    const options = this.grid.slickGrid.getOptions();
    this.grid.slickGrid.setOptions({ ...options, editable: true, autoEdit: false });
  }

  setItemMetadata(fn: (rowIndex: number) => any): void {
    if (!this.grid) return;
    this.grid.dataView.getItemMetadata = fn;
  }

  private enableFocus(enable: boolean, columnId: string) {
    if (!this.grid) return;

    const columns = this.grid.slickGrid.getColumns();
    const column = columns.find(col => col.id === columnId);

    if (!column) return;

    column.focusable = enable;
    this.grid.slickGrid.setColumns(columns);
  }

  enableSingleClickEdit() {
    const options = this.grid.slickGrid.getOptions();
    this.grid.slickGrid.setOptions({ ...options, editable: true, autoEdit: true });
  }

  showErrors(data: ICrudBulkOperationError) {
    let hasChanges = this.clearErrors();

    for (const record of data.failedRecords ?? []) {
      const row = this.grid.dataView.getItemById(record.id);

      if (row) {
        row[ROW_ERROR_MESSAGE_KEY] = record.message;
        if (data.title) {
          row[ROW_ERROR_TITLE_KEY] = data.title;
        } else {
          delete row[ROW_ERROR_TITLE_KEY];
        }
        this.grid.dataView.updateItem(record.id, row);
        hasChanges = true;
      }
    }

    if (hasChanges) {
      this.refresh();
    }
  }

  private applyOrWaitForErrors(data: ICrudBulkOperationError): void {
    if (!this.grid?.dataView) {
      return;
    }

    if (this.canApplyErrors(data)) {
      this.clearRowErrorDataViewHandler();
      this.showErrors(data);
      return;
    }

    this.waitForErrorRows(data);
  }

  private canApplyErrors(data: ICrudBulkOperationError): boolean {
    const records = data.failedRecords ?? [];

    if (records.length === 0) {
      return true;
    }

    const items = this.grid.dataView.getItems?.() ?? [];
    const isSkeletonDataset = items.some(item => item?.isSkeleton);

    if (isSkeletonDataset) {
      return false;
    }

    return records.every(record => !!this.grid.dataView.getItemById(record.id));
  }

  private waitForErrorRows(data: ICrudBulkOperationError): void {
    const dataView = this.grid.dataView;

    this.clearRowErrorDataViewHandler();

    const handler = () => {
      if (!this.grid?.dataView || this.loading() || !this.canApplyErrors(data)) {
        return;
      }

      this.clearRowErrorDataViewHandler();
      this.showErrors(data);
    };

    this.rowErrorDataViewHandler = { dataView, handler };

    dataView.onRowCountChanged?.subscribe(handler);
    dataView.onRowsChanged?.subscribe(handler);
  }

  private clearRowErrorDataViewHandler(): void {
    const entry = this.rowErrorDataViewHandler;

    if (!entry) {
      return;
    }

    entry.dataView.onRowCountChanged?.unsubscribe(entry.handler);
    entry.dataView.onRowsChanged?.unsubscribe(entry.handler);
    this.rowErrorDataViewHandler = undefined;
  }

  private clearErrors(): boolean {
    if (!this.grid?.dataView) {
      return false;
    }

    const items = this.grid.dataView.getItems?.() ?? [];
    let hasChanges = false;

    for (const item of items) {
      if (!item?.[ROW_ERROR_MESSAGE_KEY] && !item?.[ROW_ERROR_TITLE_KEY]) {
        continue;
      }

      delete item[ROW_ERROR_MESSAGE_KEY];
      delete item[ROW_ERROR_TITLE_KEY];
      this.grid.dataView.updateItem(item.id, item);
      hasChanges = true;
    }

    return hasChanges;
  }

  private getDefaultErrorTitle(): string {
    return localizeWithFallback(this.localizationService, 'AbpUi::Error');
  }
}
